/**
 * EPP (Equipo de Protección Personal) — sub-módulo de SGSSO.
 *
 * Vincula la entrega de EPP con:
 *  - Inventario   (descuento automático de stock, categoría 'epp')
 *  - Finanzas     (registro en ledger como GASTO OPEX)
 *  - Nómina       (descuento aplicable en la próxima corrida)
 *  - Análisis     (KPIs por empleado, por área, por tipo, tendencia mensual,
 *                   alertas de vida útil vencida)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, Package, ChartBar, Bell, Plus, Trash, FilePdf,
  Signature, ArrowsClockwise, Warning, User, CurrencyDollar, ClipboardText,
  Receipt, ShoppingCart, CheckCircle, XCircle, DeviceMobile, QrCode,
} from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SignaturePad from "@/components/SignaturePad";
import { QRCodeCanvas } from "qrcode.react";

const REASONS = [
  { v: "nuevo_ingreso", l: "Nuevo ingreso" },
  { v: "reposicion_programada", l: "Reposición programada" },
  { v: "reposicion_rotura", l: "Reposición por rotura/daño" },
  { v: "reposicion_perdida", l: "Reposición por pérdida" },
  { v: "renovacion", l: "Renovación de vida útil" },
  { v: "otro", l: "Otro" },
];

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;

function EppStockAlertBanner() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/epp/reorder-suggestions", { params: { lookback_days: 90 } })
      .then((r) => alive && setData(r.data))
      .catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    window.addEventListener("inventory-updated", load);
    return () => { alive = false; clearInterval(t); window.removeEventListener("inventory-updated", load); };
  }, []);
  if (!data || !data.suggestions?.length) return null;
  const critical = data.suggestions.filter((s) => s.criticality === "critical").length;
  const high = data.suggestions.filter((s) => s.criticality === "high").length;
  return (
    <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3 flex items-center gap-3 shadow-sm"
          data-testid="epp-stock-alert-banner">
      <div className="bg-red-600 text-white rounded-full p-2 shrink-0">
        <Bell size={18} weight="fill" />
      </div>
      <div className="flex-1">
        <p className="font-heading font-black text-red-900 text-sm">
          ¡Alerta de stock EPP crítico!
        </p>
        <p className="text-xs text-red-800">
          <b>{data.total_items}</b> ítem{data.total_items !== 1 ? "s" : ""} bajo mínimo
          {critical > 0 && <> · <b className="text-red-900">{critical} en cero</b></>}
          {high > 0 && <> · <b>{high} en alta criticidad</b></>}
          {" "}· Costo estimado de reposición <b>{fmtMoney(data.total_est_cost)}</b>
        </p>
      </div>
      <TabsList className="hidden">
        {/* placeholder para no romper el layout */}
      </TabsList>
    </div>
  );
}

export default function EppModule() {
  return (
    <div className="space-y-4" data-testid="hse-epp-module">
      <EppStockAlertBanner />
      <Tabs defaultValue="deliveries">
        <TabsList className="bg-slate-100 gap-1 p-1 flex-wrap h-auto">
          <TabsTrigger value="deliveries" data-testid="epp-tab-deliveries" className="gap-1.5">
            <ClipboardText size={14} weight="bold" /> Entregas
          </TabsTrigger>
          <TabsTrigger value="catalog" data-testid="epp-tab-catalog" className="gap-1.5">
            <Package size={14} weight="bold" /> Catálogo
          </TabsTrigger>
          <TabsTrigger value="deductions" data-testid="epp-tab-deductions" className="gap-1.5">
            <Receipt size={14} weight="bold" /> Descuentos
          </TabsTrigger>
          <TabsTrigger value="reorder" data-testid="epp-tab-reorder" className="gap-1.5">
            <ShoppingCart size={14} weight="bold" /> Reposición
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="epp-tab-analytics" className="gap-1.5">
            <ChartBar size={14} weight="bold" /> Análisis
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="epp-tab-alerts" className="gap-1.5">
            <Bell size={14} weight="bold" /> Alertas
          </TabsTrigger>
        </TabsList>
        <TabsContent value="deliveries" className="mt-4"><DeliveriesTab /></TabsContent>
        <TabsContent value="catalog" className="mt-4"><CatalogTab /></TabsContent>
        <TabsContent value="deductions" className="mt-4"><DeductionsTab /></TabsContent>
        <TabsContent value="reorder" className="mt-4"><ReorderTab /></TabsContent>
        <TabsContent value="analytics" className="mt-4"><AnalyticsTab /></TabsContent>
        <TabsContent value="alerts" className="mt-4"><AlertsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* =============== Entregas =============== */
function DeliveriesTab() {
  const today = new Date().toISOString().slice(0, 10);
  const start = today.slice(0, 8) + "01";
  const [range, setRange] = useState({ from: start, to: today });
  const [rows, setRows] = useState([]);
  const [openNew, setOpenNew] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/epp/deliveries", { params: range });
      setRows(r.data || []);
    } catch (e) { toast.error(apiError(e)); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (id, folio) => {
    try {
      const r = await api.get(`/epp/deliveries/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `epp-${folio || id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar entrega? Se revertirá inventario, finanzas y nómina.")) return;
    try {
      await api.delete(`/epp/deliveries/${id}`);
      toast.success("Entrega eliminada y vínculos revertidos");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="overline-label">Desde</Label>
          <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
                 className="h-9 w-40" data-testid="epp-deliveries-from" /></div>
        <div><Label className="overline-label">Hasta</Label>
          <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
                 className="h-9 w-40" data-testid="epp-deliveries-to" /></div>
        <Button variant="outline" size="sm" onClick={load} data-testid="epp-deliveries-refresh" className="gap-1.5">
          <ArrowsClockwise size={14} weight="bold" /> Actualizar
        </Button>
        <div className="flex-1" />
        <NewDeliveryDialog open={openNew} onOpenChange={setOpenNew} onDone={load} />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="epp-deliveries-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider font-bold text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Folio</th>
              <th className="text-left px-3 py-2">Fecha</th>
              <th className="text-left px-3 py-2">Colaborador</th>
              <th className="text-left px-3 py-2">Área</th>
              <th className="text-center px-3 py-2">Ítems</th>
              <th className="text-right px-3 py-2">Costo</th>
              <th className="text-center px-3 py-2">Nómina</th>
              <th className="text-right px-3 py-2">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && (
              <tr><td colSpan={8} className="py-10 text-center text-slate-400">Sin entregas en el período</td></tr>
            )}
            {rows.map((d) => (
              <tr key={d.id} data-testid={`epp-row-${d.id}`}>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-600">{d.folio}</td>
                <td className="px-3 py-2 text-slate-700">{d.date}</td>
                <td className="px-3 py-2">
                  <p className="font-semibold text-slate-800">{d.employee_name}</p>
                  <p className="text-[10px] text-slate-400">{d.employee_position} · {d.employee_crew}</p>
                </td>
                <td className="px-3 py-2 text-slate-700">{d.employee_area || "—"}</td>
                <td className="text-center px-3 py-2">
                  <span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-bold">
                    {(d.items || []).length}
                  </span>
                </td>
                <td className="text-right px-3 py-2 font-bold text-slate-800">{fmtMoney(d.total_cost)}</td>
                <td className="text-center px-3 py-2">
                  {d.charge_to_payroll ? (
                    <span className="text-[10px] uppercase font-bold text-amber-800 bg-amber-100 border border-amber-200 rounded px-2 py-0.5">
                      {fmtMoney(d.payroll_amount)}
                    </span>
                  ) : <span className="text-slate-300 text-xs">—</span>}
                </td>
                <td className="text-right px-3 py-2">
                  <div className="flex items-center gap-1 justify-end">
                    <Button size="sm" variant="outline" onClick={() => downloadPdf(d.id, d.folio)}
                            data-testid={`epp-pdf-${d.id}`}
                            className="h-7 gap-1 text-[11px] border-emerald-300 text-emerald-800 hover:bg-emerald-50">
                      <FilePdf size={12} weight="bold" /> PDF
                    </Button>
                    <button onClick={() => remove(d.id)} data-testid={`epp-del-${d.id}`}
                            className="text-red-500 hover:text-red-700 p-1" title="Eliminar (revierte todo)">
                      <Trash size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* =============== Nueva entrega =============== */
function NewDeliveryDialog({ open, onOpenChange, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [employees, setEmployees] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [date, setDate] = useState(today);
  const [items, setItems] = useState([]);
  const [chargePayroll, setChargePayroll] = useState(false);
  const [payrollAmount, setPayrollAmount] = useState(0);
  const [payrollReason, setPayrollReason] = useState("");
  const [empSig, setEmpSig] = useState("");
  const [delSig, setDelSig] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [kioskMode, setKioskMode] = useState(false);
  const [kioskSession, setKioskSession] = useState(null);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get("/hse/medical/employees").then((r) => r.data).catch(
        () => api.get("/hr/employees?active=true").then((r) => r.data).catch(() => [])),
      api.get("/epp/catalog").then((r) => r.data).catch(() => []),
      api.get("/inventory/items?category=epp&active=true").then((r) => r.data).catch(() => []),
    ]).then(([emps, cat, inv]) => {
      setEmployees(emps || []);
      setCatalog(cat || []);
      setInventoryItems(inv || []);
    });
    // reset
    setEmployeeId(""); setDate(today); setItems([]); setChargePayroll(false);
    setPayrollAmount(0); setPayrollReason(""); setEmpSig(""); setDelSig(""); setNotes("");
    setKioskMode(false); setKioskSession(null);
  }, [open]);

  const total = useMemo(() =>
    items.reduce((s, it) => s + (Number(it.qty || 0) * Number(it.unit_cost || 0)), 0),
    [items]);

  const addFromCatalog = (code) => {
    const c = catalog.find((x) => x.code === code);
    if (!c) return;
    const invMatch = inventoryItems.find((i) => i.code?.toLowerCase() === c.code.toLowerCase());
    setItems((prev) => [...prev, {
      item_code: c.code,
      item_description: c.name,
      size: c.standard_size || "",
      qty: 1,
      unit_cost: Number(invMatch?.unit_cost || c.unit_cost || 0),
      useful_life_months: Number(c.useful_life_months || 12),
      reason: "reposicion_programada",
      inventory_item_id: invMatch?.id || "",
      _stock: invMatch?.current_stock ?? null,
    }]);
  };

  const updateItem = (i, patch) => setItems((prev) => prev.map((it, j) => j === i ? { ...it, ...patch } : it));
  const removeItem = (i) => setItems((prev) => prev.filter((_, j) => j !== i));

  const save = async () => {
    if (!employeeId) { toast.error("Selecciona colaborador"); return; }
    if (!items.length) { toast.error("Agrega al menos 1 EPP"); return; }
    if (!kioskMode && !empSig) { toast.warning("Falta firma del colaborador"); return; }
    setSaving(true);
    try {
      const payload = {
        employee_id: employeeId,
        date,
        items: items.map((it) => ({
          item_code: it.item_code, item_description: it.item_description,
          size: it.size || "", qty: Number(it.qty), unit_cost: Number(it.unit_cost),
          useful_life_months: Number(it.useful_life_months || 12),
          reason: it.reason, inventory_item_id: it.inventory_item_id || "",
        })),
        delivered_by_name: "",
        delivered_by_signature: delSig,
        employee_signature: kioskMode ? "" : empSig,
        charge_to_payroll: chargePayroll,
        payroll_amount: chargePayroll ? Number(payrollAmount || 0) : 0,
        payroll_reason: payrollReason || "",
        notes,
      };
      const r = await api.post("/epp/deliveries", payload);
      if (kioskMode) {
        // Crear sesión kiosco QR
        const sess = await api.post("/epp/kiosk/sessions",
          { delivery_id: r.data.id, hours_valid: 2 });
        setKioskSession({ ...sess.data, folio: r.data.folio });
        toast.success(`Entrega ${r.data.folio} · pendiente de firma por QR`);
        onDone?.();
      } else {
        toast.success(`Entrega ${r.data.folio} guardada`);
        // Auto-descargar PDF constancia
        try {
          const pdf = await api.get(`/epp/deliveries/${r.data.id}/pdf`, { responseType: "blob" });
          const url = URL.createObjectURL(pdf.data);
          const a = document.createElement("a");
          a.href = url; a.download = `epp-${r.data.folio}.pdf`; a.click();
          URL.revokeObjectURL(url);
        } catch (_) { /* silent */ }
        onOpenChange(false);
        onDone?.();
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="gap-1.5 bg-blue-900 hover:bg-blue-800" data-testid="epp-new-btn">
          <Plus size={14} weight="bold" /> Nueva entrega de EPP
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="epp-new-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ShieldCheck size={20} weight="fill" className="text-blue-900" /> Entrega de EPP
          </DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className="overline-label">Colaborador *</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger className="h-9" data-testid="epp-emp-select"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name} · <span className="text-slate-400">{e.position || "—"} · {e.crew || "—"}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline-label">Fecha</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   className="h-9" data-testid="epp-date" />
          </div>
        </div>

        {/* Selector rápido desde catálogo */}
        <div className="mt-4 border border-slate-200 rounded-lg p-3 bg-slate-50">
          <div className="flex items-center justify-between mb-2">
            <Label className="overline-label">Agregar EPP desde catálogo</Label>
            <span className="text-[10px] text-slate-500 italic">
              Los items con stock en inventario se descontarán automáticamente
            </span>
          </div>
          <Select value="" onValueChange={addFromCatalog}>
            <SelectTrigger className="h-9 bg-white" data-testid="epp-catalog-add"><SelectValue placeholder="Elige un EPP para agregar…" /></SelectTrigger>
            <SelectContent className="bg-white max-h-72">
              {catalog.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.code} · {c.name} <span className="text-slate-400">· {fmtMoney(c.unit_cost)} · vida útil {c.useful_life_months}m</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Items agregados */}
        {items.length > 0 && (
          <div className="mt-3 border border-slate-200 rounded-lg overflow-x-auto bg-white">
            <table className="w-full text-sm" data-testid="epp-items-table">
              <thead className="bg-slate-50 text-[11px] uppercase font-bold text-slate-500">
                <tr>
                  <th className="text-left px-2 py-2">EPP</th>
                  <th className="text-left px-2 py-2">Talla</th>
                  <th className="text-center px-2 py-2">Cant.</th>
                  <th className="text-right px-2 py-2">Costo unit.</th>
                  <th className="text-center px-2 py-2">Vida útil</th>
                  <th className="text-left px-2 py-2">Motivo</th>
                  <th className="text-right px-2 py-2">Subt.</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it, i) => (
                  <tr key={i} data-testid={`epp-item-row-${i}`}>
                    <td className="px-2 py-1.5">
                      <p className="font-semibold text-slate-800 text-[13px]">{it.item_description}</p>
                      <p className="text-[10px] text-slate-500 font-mono">{it.item_code}
                        {it._stock !== null && it._stock !== undefined && (
                          <span className={`ml-2 ${it._stock >= it.qty ? "text-emerald-700" : "text-red-700"}`}>
                            stock: {it._stock}
                          </span>
                        )}
                      </p>
                    </td>
                    <td className="px-2 py-1.5">
                      <Input value={it.size || ""} onChange={(e) => updateItem(i, { size: e.target.value })}
                             className="h-8 w-24 text-[12px]" data-testid={`epp-size-${i}`} />
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <Input type="number" min="1" step="1" value={it.qty}
                             onChange={(e) => updateItem(i, { qty: Number(e.target.value) })}
                             className="h-8 w-16 text-[12px] text-center" data-testid={`epp-qty-${i}`} />
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <Input type="number" min="0" step="10" value={it.unit_cost}
                             onChange={(e) => updateItem(i, { unit_cost: Number(e.target.value) })}
                             className="h-8 w-24 text-[12px] text-right" data-testid={`epp-cost-${i}`} />
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <Input type="number" min="1" value={it.useful_life_months}
                             onChange={(e) => updateItem(i, { useful_life_months: Number(e.target.value) })}
                             className="h-8 w-16 text-[12px] text-center" data-testid={`epp-life-${i}`} />
                      <span className="text-[9px] text-slate-400 ml-1">meses</span>
                    </td>
                    <td className="px-2 py-1.5">
                      <Select value={it.reason} onValueChange={(v) => updateItem(i, { reason: v })}>
                        <SelectTrigger className="h-8 text-[12px]" data-testid={`epp-reason-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {REASONS.map((r) => (<SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-2 py-1.5 text-right font-bold text-slate-800">
                      {fmtMoney(Number(it.qty || 0) * Number(it.unit_cost || 0))}
                    </td>
                    <td>
                      <button onClick={() => removeItem(i)} className="text-red-500 hover:text-red-700 p-1"
                              data-testid={`epp-item-del-${i}`}><Trash size={14} /></button>
                    </td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-bold">
                  <td colSpan={6} className="px-2 py-2 text-right">TOTAL</td>
                  <td className="px-2 py-2 text-right text-emerald-800">{fmtMoney(total)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Cargo a nómina */}
        <div className="mt-3 border border-amber-200 rounded-lg p-3 bg-amber-50/40">
          <div className="flex items-center gap-3">
            <Switch checked={chargePayroll} onCheckedChange={setChargePayroll} data-testid="epp-charge-payroll" />
            <div>
              <p className="text-sm font-semibold text-slate-800">Cargar a nómina del colaborador</p>
              <p className="text-[11px] text-slate-500">Genera un descuento pendiente para la próxima corrida de nómina.</p>
            </div>
          </div>
          {chargePayroll && (
            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <div><Label className="overline-label">Monto a descontar</Label>
                <Input type="number" min="0" step="10" value={payrollAmount}
                       onChange={(e) => setPayrollAmount(Number(e.target.value))}
                       className="h-9" data-testid="epp-payroll-amount" /></div>
              <div><Label className="overline-label">Motivo del descuento</Label>
                <Input value={payrollReason} onChange={(e) => setPayrollReason(e.target.value)}
                       placeholder="Ej: 2da reposición de guantes en el mes"
                       className="h-9" data-testid="epp-payroll-reason" /></div>
            </div>
          )}
        </div>

        {/* Modo de firma */}
        <div className="mt-3 border border-blue-200 rounded-lg p-3 bg-blue-50/40">
          <div className="flex items-center gap-3">
            <Switch checked={kioskMode} onCheckedChange={setKioskMode} data-testid="epp-kiosk-mode" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                <QrCode size={14} weight="fill" className="text-blue-900" /> Firma por QR desde móvil del colaborador
              </p>
              <p className="text-[11px] text-slate-500">
                {kioskMode
                  ? "La constancia quedará pendiente de firma. Se genera un QR para que el colaborador firme desde su celular."
                  : "Firma directa en el tablet del almacén (default)."}
              </p>
            </div>
          </div>
        </div>

        {/* Firmas */}
        <div className={`grid md:grid-cols-2 gap-3 mt-3 ${kioskMode ? "opacity-40 pointer-events-none" : ""}`}>
          <div className="border border-slate-200 rounded-lg p-2 bg-white">
            <p className="text-[10px] uppercase font-bold text-slate-500 mb-1 flex items-center gap-1">
              <User size={11} weight="bold" /> Firma del colaborador (receptor) {kioskMode ? "· por QR" : "*"}
            </p>
            <SignaturePad value={empSig} onChange={setEmpSig} width={340} height={110} testid="epp-emp-sig" />
          </div>
          <div className="border border-slate-200 rounded-lg p-2 bg-white">
            <p className="text-[10px] uppercase font-bold text-slate-500 mb-1 flex items-center gap-1">
              <Signature size={11} weight="bold" /> Firma del entregador (almacén/SGSSO)
            </p>
            <SignaturePad value={delSig} onChange={setDelSig} width={340} height={110} testid="epp-del-sig" />
          </div>
        </div>

        <div>
          <Label className="overline-label">Notas</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                 placeholder="Observaciones adicionales, número de tallas, etc."
                 className="h-9" data-testid="epp-notes" />
        </div>

        <div className="flex justify-between items-center pt-3 border-t border-slate-200">
          <p className="text-[11px] text-slate-500">
            El registro descontará stock de inventario, registrará el costo en finanzas
            {chargePayroll ? " y creará un descuento pendiente en nómina" : ""}.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="epp-save-btn"
                    className="bg-blue-900 hover:bg-blue-800 gap-1.5">
              {kioskMode
                ? <><QrCode size={14} weight="bold" /> {saving ? "Generando…" : "Registrar y generar QR"}</>
                : <><ShieldCheck size={14} weight="bold" /> {saving ? "Guardando…" : "Registrar entrega"}</>}
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Dialog QR — se muestra al guardar entrega en modo kiosk */}
      {kioskSession && (
        <Dialog open onOpenChange={() => { setKioskSession(null); onOpenChange(false); }}>
          <DialogContent className="bg-white max-w-md text-center" data-testid="epp-qr-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center justify-center gap-2">
                <QrCode size={20} weight="fill" className="text-blue-900" /> Firma por QR
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">
              <b>{kioskSession.employee_name}</b> · Folio <span className="font-mono">{kioskSession.folio}</span>
            </p>
            <p className="text-[11px] text-slate-500 -mt-1">
              Muestra este QR al colaborador. Válido hasta las {kioskSession.expires_at?.slice(11, 16)} UTC.
            </p>
            <div className="flex justify-center py-4">
              <div className="bg-white p-3 border-4 border-blue-900 rounded-lg" data-testid="epp-qr-canvas">
                <QRCodeCanvas value={kioskSession.mobile_url} size={220} level="M" />
              </div>
            </div>
            <p className="text-[10px] text-slate-400 font-mono break-all px-4">
              {kioskSession.mobile_url}
            </p>
            <div className="flex justify-center gap-2 pt-3">
              <Button variant="outline" onClick={() => {
                navigator.clipboard?.writeText(kioskSession.mobile_url);
                toast.success("URL copiada");
              }} data-testid="epp-qr-copy">Copiar URL</Button>
              <Button onClick={() => { setKioskSession(null); onOpenChange(false); }}
                      data-testid="epp-qr-done"
                      className="bg-emerald-700 hover:bg-emerald-800 gap-1.5">
                <CheckCircle size={14} weight="bold" /> Listo
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

/* =============== Catálogo =============== */
function CatalogTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/epp/catalog").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    try {
      if (editing?.id) await api.put(`/epp/catalog/${editing.id}`, payload);
      else await api.post("/epp/catalog", payload);
      toast.success("Guardado");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Archivar este EPP del catálogo?")) return;
    try { await api.delete(`/epp/catalog/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setEditing({})} data-testid="epp-cat-new"
                className="bg-blue-900 hover:bg-blue-800 gap-1.5">
          <Plus size={14} weight="bold" /> Nuevo EPP
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="epp-cat-table">
          <thead className="bg-slate-50 text-[11px] uppercase font-bold text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Código</th>
              <th className="text-left px-3 py-2">Nombre</th>
              <th className="text-left px-3 py-2">Categoría</th>
              <th className="text-center px-3 py-2">Vida útil</th>
              <th className="text-right px-3 py-2">Costo</th>
              <th className="text-left px-3 py-2">Talla</th>
              <th className="text-center px-3 py-2" title="Stock actual (inventario)">Stock</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((c) => {
              const stockKnown = typeof c.current_stock === "number";
              const stockLow = stockKnown && c.min_stock != null && c.current_stock <= c.min_stock;
              return (
                <tr key={c.id}>
                  <td className="px-3 py-2 font-mono text-[11px] text-slate-600">{c.code}</td>
                  <td className="px-3 py-2 font-semibold text-slate-800">{c.name}</td>
                  <td className="px-3 py-2 text-slate-600 capitalize">{c.category}</td>
                  <td className="text-center px-3 py-2">{c.useful_life_months}m</td>
                  <td className="text-right px-3 py-2">{fmtMoney(c.unit_cost)}</td>
                  <td className="px-3 py-2 text-slate-600">{c.standard_size}</td>
                  <td className="text-center px-3 py-2 whitespace-nowrap" data-testid={`epp-cat-stock-${c.id}`}>
                    {!stockKnown ? (
                      <span className="text-[10px] text-slate-400 italic">no vinculado</span>
                    ) : (
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-mono font-bold border ${
                        stockLow ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"
                      }`}>
                        {c.current_stock}
                        {stockLow && <span title={`Mínimo: ${c.min_stock}`}>⚠</span>}
                      </span>
                    )}
                  </td>
                  <td className="text-right px-3 py-2 whitespace-nowrap">
                    <a href={`/inventory?item=${c.inventory_item_id || ""}`}
                       target="_blank" rel="noreferrer"
                       className="text-[10px] text-slate-500 hover:text-blue-700 mr-2"
                       data-testid={`epp-cat-inv-link-${c.id}`}
                       title="Abrir en inventario">📦</a>
                    <button onClick={() => setEditing(c)} className="text-blue-700 hover:underline text-xs mr-2"
                            data-testid={`epp-cat-edit-${c.id}`}>Editar</button>
                    <button onClick={() => remove(c.id)} className="text-red-500 hover:text-red-700 p-1"
                            data-testid={`epp-cat-del-${c.id}`}><Trash size={14} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-500 italic">
        <span className="font-bold">Vínculo con Inventario:</span> Cada EPP del catálogo se sincroniza automáticamente con un ítem de inventario (categoría <code>epp</code>). Editar el nombre/costo aquí actualiza el ítem; el stock se ajusta desde Inventario o al entregar.
      </p>
      {editing && <CatalogFormDialog value={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function CatalogFormDialog({ value, onClose, onSave }) {
  const [f, setF] = useState({
    code: value?.code || "",
    name: value?.name || "",
    category: value?.category || "otros",
    useful_life_months: value?.useful_life_months ?? 12,
    unit_cost: value?.unit_cost ?? 0,
    standard_size: value?.standard_size || "Único",
    active: value?.active !== false,
    notes: value?.notes || "",
  });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg" data-testid="epp-cat-dialog">
        <DialogHeader><DialogTitle>{value?.id ? "Editar" : "Nuevo"} EPP</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline-label">Código *</Label>
              <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })}
                     className="h-9" data-testid="epp-cat-code" /></div>
            <div><Label className="overline-label">Categoría</Label>
              <Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}
                     placeholder="cabeza, pie, mano..." className="h-9" data-testid="epp-cat-category" /></div>
          </div>
          <div><Label className="overline-label">Nombre *</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                   className="h-9" data-testid="epp-cat-name" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="overline-label">Vida útil (meses)</Label>
              <Input type="number" value={f.useful_life_months}
                     onChange={(e) => setF({ ...f, useful_life_months: Number(e.target.value) })}
                     className="h-9" data-testid="epp-cat-life" /></div>
            <div><Label className="overline-label">Costo unitario</Label>
              <Input type="number" value={f.unit_cost}
                     onChange={(e) => setF({ ...f, unit_cost: Number(e.target.value) })}
                     className="h-9" data-testid="epp-cat-cost" /></div>
            <div><Label className="overline-label">Talla estándar</Label>
              <Input value={f.standard_size} onChange={(e) => setF({ ...f, standard_size: e.target.value })}
                     className="h-9" data-testid="epp-cat-size" /></div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => onSave(f)} className="bg-blue-900 hover:bg-blue-800" data-testid="epp-cat-save">
            Guardar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* =============== Analytics =============== */
function AnalyticsTab() {
  const today = new Date().toISOString().slice(0, 10);
  const start = new Date(); start.setMonth(start.getMonth() - 3);
  const [range, setRange] = useState({ from: start.toISOString().slice(0, 10), to: today });
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get("/epp/analytics/summary", { params: range }); setData(r.data); }
    catch (e) { toast.error(apiError(e)); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-slate-400 text-sm italic">Cargando…</div>;
  const t = data.totals || {};
  const maxCost = Math.max(1, ...(data.by_type || []).map((r) => r.cost));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="overline-label">Desde</Label>
          <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
                 className="h-9 w-40" data-testid="epp-an-from" /></div>
        <div><Label className="overline-label">Hasta</Label>
          <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
                 className="h-9 w-40" data-testid="epp-an-to" /></div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Entregas" value={t.deliveries || 0} icon={ClipboardText} />
        <Kpi label="Unidades" value={Math.round(t.units || 0)} icon={Package} />
        <Kpi label="Costo total" value={fmtMoney(t.cost)} icon={CurrencyDollar} accent="emerald" />
        <Kpi label="Cargo a nómina" value={fmtMoney(t.payroll_charge)} icon={Warning} accent="amber" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <p className="font-heading text-sm font-bold text-slate-800 mb-3">Consumo por tipo de EPP · Costo</p>
          {(data.by_type || []).slice(0, 10).map((r) => (
            <div key={r.code} className="mb-2 last:mb-0" data-testid={`epp-an-type-${r.code}`}>
              <div className="flex justify-between text-[11px] text-slate-600 mb-0.5">
                <span className="truncate mr-2">{r.desc}</span>
                <span className="font-bold">{fmtMoney(r.cost)} · {Math.round(r.qty)}u</span>
              </div>
              <div className="h-2 bg-slate-100 rounded overflow-hidden">
                <div className="h-full bg-blue-800" style={{ width: `${(r.cost / maxCost) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <p className="font-heading text-sm font-bold text-slate-800 mb-3">Consumo por área</p>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase text-slate-500 font-bold">
              <tr><th className="text-left py-1">Área</th><th className="text-center">Entregas</th>
                  <th className="text-center">Uds.</th><th className="text-right">Costo</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data.by_area || []).map((r) => (
                <tr key={r.area}>
                  <td className="py-1.5">{r.area}</td>
                  <td className="text-center">{r.deliveries}</td>
                  <td className="text-center">{Math.round(r.units)}</td>
                  <td className="text-right font-bold text-emerald-700">{fmtMoney(r.cost)}</td>
                </tr>
              ))}
              {!data.by_area?.length && <tr><td colSpan={4} className="text-center py-4 text-slate-400 italic">Sin datos</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <p className="font-heading text-sm font-bold text-slate-800 mb-3">Top 10 colaboradores por consumo de EPP</p>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase text-slate-500 font-bold">
            <tr>
              <th className="text-left py-1">#</th><th className="text-left">Colaborador</th>
              <th className="text-left">Puesto</th><th className="text-center">Entregas</th>
              <th className="text-center">Unidades</th><th className="text-right">Costo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data.top_employees || []).map((r, i) => (
              <tr key={r.employee_id} data-testid={`epp-an-emp-${r.employee_id}`}>
                <td className="py-1.5">{i + 1}</td>
                <td className="font-semibold text-slate-800">{r.name}</td>
                <td className="text-slate-600">{r.position}</td>
                <td className="text-center">{r.deliveries}</td>
                <td className="text-center">{Math.round(r.units)}</td>
                <td className="text-right font-bold text-emerald-700">{fmtMoney(r.cost)}</td>
              </tr>
            ))}
            {!data.top_employees?.length && (
              <tr><td colSpan={6} className="text-center py-4 text-slate-400 italic">Sin datos</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <p className="font-heading text-sm font-bold text-slate-800 mb-3">Tendencia mensual (costo)</p>
        <MonthlyBar data={data.monthly || []} />
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent = "blue" }) {
  const map = {
    blue: "border-blue-200 text-blue-900 bg-blue-50",
    emerald: "border-emerald-200 text-emerald-900 bg-emerald-50",
    amber: "border-amber-200 text-amber-900 bg-amber-50",
  };
  return (
    <div className={`border rounded-lg p-3 ${map[accent] || map.blue}`}>
      <div className="flex items-center gap-2">
        <Icon size={18} weight="bold" />
        <p className="text-[11px] uppercase font-bold tracking-wider opacity-80">{label}</p>
      </div>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}

function MonthlyBar({ data }) {
  if (!data.length) return <p className="text-slate-400 italic text-sm">Sin datos</p>;
  const max = Math.max(1, ...data.map((d) => d.cost));
  return (
    <div className="flex items-end gap-2 h-40">
      {data.map((d) => (
        <div key={d.month} className="flex-1 flex flex-col items-center gap-1">
          <div className="text-[10px] text-slate-600 font-bold">{fmtMoney(d.cost)}</div>
          <div className="w-full bg-blue-800 rounded-t" style={{ height: `${(d.cost / max) * 100}%`, minHeight: 2 }} />
          <div className="text-[10px] text-slate-500">{d.month}</div>
        </div>
      ))}
    </div>
  );
}

/* =============== Alertas =============== */
function AlertsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/epp/analytics/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <div className="text-slate-400 italic">Cargando…</div>;
  const rows = data.expiring_soon || [];
  return (
    <div className="space-y-3">
      <div className="border border-amber-200 rounded-lg bg-amber-50/40 p-3 flex items-center gap-3">
        <Warning size={22} weight="fill" className="text-amber-700" />
        <div>
          <p className="font-heading font-bold text-amber-900">Renovaciones próximas</p>
          <p className="text-xs text-amber-700">
            EPP cuya vida útil está por vencer (≤ 1 mes de uso restante).
            {data.expiring_count > rows.length && ` Se muestran los primeros ${rows.length} de ${data.expiring_count}.`}
          </p>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase font-bold text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Colaborador</th>
              <th className="text-left px-3 py-2">EPP</th>
              <th className="text-center px-3 py-2">Última entrega</th>
              <th className="text-center px-3 py-2">Meses usados</th>
              <th className="text-center px-3 py-2">Vida útil</th>
              <th className="text-center px-3 py-2">Restante</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && (
              <tr><td colSpan={6} className="py-10 text-center text-emerald-700 italic">
                ✓ Sin renovaciones vencidas — todo dentro de la vida útil
              </td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} data-testid={`epp-alert-${i}`}>
                <td className="px-3 py-2 font-semibold text-slate-800">{r.employee_name}</td>
                <td className="px-3 py-2 text-slate-700">{r.item_description}
                  <span className="text-[10px] font-mono text-slate-400 ml-2">{r.item_code}</span></td>
                <td className="text-center px-3 py-2 text-slate-600">{r.last_delivery}</td>
                <td className="text-center px-3 py-2">{r.months_used}</td>
                <td className="text-center px-3 py-2">{r.useful_life_months}m</td>
                <td className="text-center px-3 py-2">
                  <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold ${
                    r.months_remaining <= 0
                      ? "bg-red-100 text-red-800 border border-red-300"
                      : "bg-amber-100 text-amber-800 border border-amber-300"
                  }`}>
                    {r.months_remaining <= 0 ? "VENCIDO" : `${r.months_remaining}m`}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* =============== Descuentos EPP (aprobación en lote) =============== */
function DeductionsTab() {
  const today = new Date().toISOString().slice(0, 10);
  const start = new Date(); start.setDate(start.getDate() - 30);
  const iso = (d) => d.toISOString().slice(0, 10);
  const [range, setRange] = useState({ from: iso(start), to: today });
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    try {
      const params = { date_from: range.from, date_to: range.to };
      if (status) params.status = status;
      const r = await api.get("/epp/deductions", { params });
      setRows(r.data || []);
      setSelected(new Set());
    } catch (e) { toast.error(apiError(e)); }
  }, [range, status]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };

  const bulkApprove = async () => {
    if (selected.size === 0) { toast.warning("Selecciona al menos uno"); return; }
    if (!window.confirm(`¿Aprobar ${selected.size} descuento(s) para la próxima corrida?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/epp/deductions/bulk-apply", { ids: [...selected] });
      toast.success(`${r.data.modified} descuento(s) aprobado(s)`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const bulkReject = async () => {
    if (selected.size === 0) { toast.warning("Selecciona al menos uno"); return; }
    if (!rejectReason.trim()) { toast.error("Indica el motivo del rechazo"); return; }
    setBusy(true);
    try {
      const r = await api.post("/epp/deductions/bulk-reject",
        { ids: [...selected], reason: rejectReason.trim() });
      toast.success(`${r.data.modified} descuento(s) rechazado(s)`);
      setRejectOpen(false);
      setRejectReason("");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const totalSel = useMemo(() =>
    rows.filter((r) => selected.has(r.id)).reduce((s, r) => s + Number(r.amount || 0), 0),
    [rows, selected]);
  const totalPending = useMemo(() =>
    rows.filter((r) => r.status === "pending").reduce((s, r) => s + Number(r.amount || 0), 0),
    [rows]);

  return (
    <div className="space-y-4" data-testid="epp-deductions-tab">
      <div className="border border-amber-200 rounded-lg bg-amber-50/40 p-3 flex items-center gap-3">
        <Receipt size={22} weight="fill" className="text-amber-700" />
        <div className="flex-1">
          <p className="font-heading font-bold text-amber-900">Aprobación de descuentos EPP en lote</p>
          <p className="text-xs text-amber-700">
            El jefe de nómina revisa los descuentos generados por entregas de EPP
            y los aprueba (o rechaza) antes de la próxima corrida.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="overline-label">Desde</Label>
          <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
                 className="h-9 w-40" data-testid="epp-ded-from" /></div>
        <div><Label className="overline-label">Hasta</Label>
          <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
                 className="h-9 w-40" data-testid="epp-ded-to" /></div>
        <div><Label className="overline-label">Estado</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-40" data-testid="epp-ded-status"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="pending">Pendientes</SelectItem>
              <SelectItem value="applied">Aplicados</SelectItem>
              <SelectItem value="rejected">Rechazados</SelectItem>
            </SelectContent>
          </Select></div>
        <Button variant="outline" size="sm" onClick={load} data-testid="epp-ded-refresh" className="gap-1.5">
          <ArrowsClockwise size={14} weight="bold" /> Actualizar
        </Button>
        <div className="flex-1" />
        <div className="text-right text-xs">
          <div className="text-slate-500">Seleccionados: <b className="text-slate-900">{selected.size}</b> · {fmtMoney(totalSel)}</div>
          <div className="text-slate-500">Total pendiente: <b className="text-amber-800">{fmtMoney(totalPending)}</b></div>
        </div>
      </div>

      {status === "pending" && rows.length > 0 && (
        <div className="flex gap-2">
          <Button onClick={bulkApprove} disabled={busy || selected.size === 0}
                  data-testid="epp-ded-approve"
                  className="bg-emerald-700 hover:bg-emerald-800 gap-1.5">
            <CheckCircle size={14} weight="bold" /> Aprobar seleccionados ({selected.size})
          </Button>
          <Button variant="outline" onClick={() => setRejectOpen(true)}
                  disabled={busy || selected.size === 0}
                  data-testid="epp-ded-reject"
                  className="gap-1.5 border-red-300 text-red-700 hover:bg-red-50">
            <XCircle size={14} weight="bold" /> Rechazar seleccionados
          </Button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="epp-ded-table">
          <thead className="bg-slate-50 text-[11px] uppercase font-bold text-slate-500">
            <tr>
              <th className="px-3 py-2">
                {status === "pending" && rows.length > 0 && (
                  <input type="checkbox" checked={selected.size === rows.length && rows.length > 0}
                         onChange={toggleAll} data-testid="epp-ded-select-all" />
                )}
              </th>
              <th className="text-left px-3 py-2">Fecha</th>
              <th className="text-left px-3 py-2">Colaborador</th>
              <th className="text-left px-3 py-2">Concepto</th>
              <th className="text-left px-3 py-2">Ref. EPP</th>
              <th className="text-right px-3 py-2">Monto</th>
              <th className="text-center px-3 py-2">Estado</th>
              <th className="text-left px-3 py-2">Aprobado por</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && (
              <tr><td colSpan={8} className="py-10 text-center text-slate-400">
                {status === "pending" ? "✓ Sin descuentos pendientes" : "Sin descuentos en este estado"}
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`epp-ded-row-${r.id}`}
                  className={selected.has(r.id) ? "bg-blue-50" : ""}>
                <td className="px-3 py-2">
                  {r.status === "pending" && (
                    <input type="checkbox" checked={selected.has(r.id)}
                           onChange={() => toggle(r.id)}
                           data-testid={`epp-ded-check-${r.id}`} />
                  )}
                </td>
                <td className="px-3 py-2 text-slate-700">{r.date}</td>
                <td className="px-3 py-2 font-semibold text-slate-800">{r.employee_name}</td>
                <td className="px-3 py-2 text-slate-700 text-[13px]">{r.concept}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{r.reference}</td>
                <td className="text-right px-3 py-2 font-bold text-slate-800">{fmtMoney(r.amount)}</td>
                <td className="text-center px-3 py-2">
                  <StatusBadge status={r.status} />
                </td>
                <td className="px-3 py-2 text-[11px] text-slate-500">
                  {r.applied_by || r.rejected_by || "—"}
                  {r.rejection_reason && (
                    <div className="text-[10px] text-red-700 mt-0.5">{r.rejection_reason}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="epp-ded-reject-dialog">
          <DialogHeader><DialogTitle>Rechazar descuentos</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 mb-2">
            Se rechazarán {selected.size} descuento(s). El motivo queda registrado.
          </p>
          <Label className="overline-label">Motivo del rechazo *</Label>
          <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                 placeholder="Ej: Reposición autorizada por SGSSO, no procede cobro"
                 data-testid="epp-ded-reject-reason" />
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancelar</Button>
            <Button onClick={bulkReject} disabled={busy || !rejectReason.trim()}
                    data-testid="epp-ded-reject-confirm"
                    className="bg-red-600 hover:bg-red-700 gap-1.5">
              <XCircle size={14} weight="bold" /> Confirmar rechazo
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { bg: "bg-amber-100 text-amber-800 border-amber-300", l: "Pendiente" },
    applied: { bg: "bg-emerald-100 text-emerald-800 border-emerald-300", l: "Aplicado" },
    rejected: { bg: "bg-red-100 text-red-800 border-red-300", l: "Rechazado" },
  };
  const s = map[status] || { bg: "bg-slate-100 text-slate-800", l: status };
  return (
    <span className={`inline-block text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${s.bg}`}>
      {s.l}
    </span>
  );
}

/* =============== Reposición automática =============== */
function ReorderTab() {
  const [lookback, setLookback] = useState(90);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [poOpen, setPoOpen] = useState(false);
  const [poForm, setPoForm] = useState({ supplier: "", supplier_rfc: "", expected_delivery: "", notes: "" });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/epp/reorder-suggestions", { params: { lookback_days: lookback } });
      setData(r.data);
      setSelected(new Set(r.data.suggestions.map((s) => s.item_id)));  // pre-select all
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  }, [lookback]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (!data) return;
    if (selected.size === data.suggestions.length) setSelected(new Set());
    else setSelected(new Set(data.suggestions.map((s) => s.item_id)));
  };

  const createPO = async () => {
    if (selected.size === 0) { toast.warning("Selecciona al menos un ítem"); return; }
    setBusy(true);
    try {
      const r = await api.post("/epp/reorder-suggestions/create-po",
        { item_ids: [...selected], ...poForm });
      toast.success(`OC ${r.data.po_number} creada · ${fmtMoney(r.data.totals?.total)}`);
      setPoOpen(false);
      setPoForm({ supplier: "", supplier_rfc: "", expected_delivery: "", notes: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const selectedTotal = useMemo(() => {
    if (!data) return 0;
    return data.suggestions.filter((s) => selected.has(s.item_id))
      .reduce((sum, s) => sum + Number(s.est_cost || 0), 0);
  }, [data, selected]);

  const critMap = {
    critical: "bg-red-100 text-red-800 border-red-300",
    high: "bg-amber-100 text-amber-800 border-amber-300",
    medium: "bg-blue-100 text-blue-800 border-blue-300",
  };
  const critLabel = { critical: "CRÍTICO", high: "ALTO", medium: "MEDIO" };

  return (
    <div className="space-y-4" data-testid="epp-reorder-tab">
      <div className="border border-red-200 rounded-lg bg-red-50/40 p-3 flex items-center gap-3">
        <ShoppingCart size={22} weight="fill" className="text-red-700" />
        <div className="flex-1">
          <p className="font-heading font-bold text-red-900">Reposición automática de EPP</p>
          <p className="text-xs text-red-700">
            Items de inventario (categoría &apos;epp&apos;) cuyo stock ≤ mínimo tras las entregas
            recientes. Selecciona y genera una orden de compra sugerida.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="overline-label">Ventana de consumo (días)</Label>
          <Select value={String(lookback)} onValueChange={(v) => setLookback(Number(v))}>
            <SelectTrigger className="h-9 w-40" data-testid="epp-reorder-lookback"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="30">30 días</SelectItem>
              <SelectItem value="60">60 días</SelectItem>
              <SelectItem value="90">90 días</SelectItem>
              <SelectItem value="180">180 días</SelectItem>
            </SelectContent>
          </Select></div>
        <Button variant="outline" size="sm" onClick={load} disabled={busy}
                data-testid="epp-reorder-refresh" className="gap-1.5">
          <ArrowsClockwise size={14} weight="bold" /> Actualizar
        </Button>
        <div className="flex-1" />
        {data && data.suggestions.length > 0 && (
          <>
            <div className="text-right text-xs">
              <div className="text-slate-500">Seleccionados: <b className="text-slate-900">{selected.size}</b></div>
              <div className="text-slate-500">Costo estimado: <b className="text-emerald-800">{fmtMoney(selectedTotal)}</b></div>
            </div>
            <Button onClick={() => setPoOpen(true)} disabled={selected.size === 0}
                    data-testid="epp-reorder-po-btn"
                    className="bg-blue-900 hover:bg-blue-800 gap-1.5">
              <ShoppingCart size={14} weight="bold" /> Generar OC ({selected.size})
            </Button>
          </>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="epp-reorder-table">
          <thead className="bg-slate-50 text-[11px] uppercase font-bold text-slate-500">
            <tr>
              <th className="px-3 py-2">
                {data && data.suggestions.length > 0 && (
                  <input type="checkbox"
                         checked={data.suggestions.length > 0 && selected.size === data.suggestions.length}
                         onChange={toggleAll} data-testid="epp-reorder-select-all" />
                )}
              </th>
              <th className="text-left px-3 py-2">Código</th>
              <th className="text-left px-3 py-2">EPP</th>
              <th className="text-center px-3 py-2">Criticidad</th>
              <th className="text-center px-3 py-2">Stock actual</th>
              <th className="text-center px-3 py-2">Mín / Máx</th>
              <th className="text-center px-3 py-2">Días stock</th>
              <th className="text-center px-3 py-2">Sugerido</th>
              <th className="text-right px-3 py-2">Costo est.</th>
              <th className="text-left px-3 py-2">Proveedor</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(!data || data.suggestions.length === 0) && (
              <tr><td colSpan={10} className="py-10 text-center text-emerald-700 italic">
                ✓ Todos los EPP están dentro de stock — no requieren reposición
              </td></tr>
            )}
            {data?.suggestions.map((s) => (
              <tr key={s.item_id} data-testid={`epp-reorder-row-${s.item_id}`}
                  className={selected.has(s.item_id) ? "bg-blue-50" : ""}>
                <td className="px-3 py-2">
                  <input type="checkbox" checked={selected.has(s.item_id)}
                         onChange={() => toggle(s.item_id)}
                         data-testid={`epp-reorder-check-${s.item_id}`} />
                </td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-600">{s.item_code}</td>
                <td className="px-3 py-2 font-semibold text-slate-800">{s.description}</td>
                <td className="text-center px-3 py-2">
                  <span className={`inline-block text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${critMap[s.criticality]}`}>
                    {critLabel[s.criticality]}
                  </span>
                </td>
                <td className="text-center px-3 py-2 font-bold text-red-700">{s.current_stock}</td>
                <td className="text-center px-3 py-2 text-slate-600">{s.min_stock} / {s.max_stock}</td>
                <td className="text-center px-3 py-2 text-[11px]">
                  {s.days_of_stock_left !== null ? `${s.days_of_stock_left}d` :
                    <span className="italic text-slate-300">s/consumo</span>}
                </td>
                <td className="text-center px-3 py-2 font-bold text-blue-900">
                  {s.suggested_qty} <span className="text-[10px] text-slate-400 font-normal">{s.unit}</span>
                </td>
                <td className="text-right px-3 py-2 font-bold text-emerald-700">{fmtMoney(s.est_cost)}</td>
                <td className="px-3 py-2 text-slate-600 text-[12px]">{s.supplier || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={poOpen} onOpenChange={setPoOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="epp-reorder-po-dialog">
          <DialogHeader><DialogTitle className="font-heading">Generar Orden de Compra</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 mb-2">
            {selected.size} ítem(s) · Total estimado {fmtMoney(selectedTotal)} + IVA
          </p>
          <div className="space-y-3">
            <div><Label className="overline-label">Proveedor</Label>
              <Input value={poForm.supplier} onChange={(e) => setPoForm({ ...poForm, supplier: e.target.value })}
                     placeholder="Nombre del proveedor" className="h-9"
                     data-testid="epp-reorder-po-supplier" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="overline-label">RFC</Label>
                <Input value={poForm.supplier_rfc}
                       onChange={(e) => setPoForm({ ...poForm, supplier_rfc: e.target.value })}
                       className="h-9" data-testid="epp-reorder-po-rfc" /></div>
              <div><Label className="overline-label">Entrega esperada</Label>
                <Input type="date" value={poForm.expected_delivery}
                       onChange={(e) => setPoForm({ ...poForm, expected_delivery: e.target.value })}
                       className="h-9" data-testid="epp-reorder-po-eta" /></div>
            </div>
            <div><Label className="overline-label">Notas</Label>
              <Input value={poForm.notes}
                     onChange={(e) => setPoForm({ ...poForm, notes: e.target.value })}
                     placeholder="Observaciones para la OC" className="h-9"
                     data-testid="epp-reorder-po-notes" /></div>
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={() => setPoOpen(false)}>Cancelar</Button>
            <Button onClick={createPO} disabled={busy}
                    data-testid="epp-reorder-po-create"
                    className="bg-blue-900 hover:bg-blue-800 gap-1.5">
              <ShoppingCart size={14} weight="bold" /> {busy ? "Creando…" : "Crear OC"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

